import { useEffect, useId, useRef, useState, useCallback } from 'react';
import { Box, IconButton, Stack, Typography, alpha, CircularProgress, Dialog, useTheme } from '@mui/material';
import { ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, Crosshair as FitIcon, Maximize2 as FullscreenIcon, X as CloseIcon } from 'lucide-react';
import mermaid from 'mermaid';

let globalMermaidId = 0;

interface InteractiveMermaidProps {
  chart: string;
  compact?: boolean; // smaller toolbar for chat context
}

export default function InteractiveMermaid({ chart, compact = false }: InteractiveMermaidProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgWrapRef = useRef<HTMLDivElement>(null);
  const uniqueId = `im-${++globalMermaidId}`;
  const [svgContent, setSvgContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let cleaned = chart.trim();
        if (cleaned.startsWith('```')) {
          cleaned = cleaned.replace(/^```(?:mermaid)?\s*/, '').replace(/```\s*$/, '').trim();
        }
        const { svg } = await mermaid.render(`mermaid-${uniqueId}`, cleaned);
        if (!cancelled) { setSvgContent(svg); setError(null); }
      } catch (err: any) {
        // Mermaid injects an error SVG into the DOM on syntax errors — remove it
        document.querySelectorAll(`[id*="mermaid-${uniqueId}"]`).forEach(el => el.remove());
        if (!cancelled) { setError(err.message || 'Failed to render'); setSvgContent(''); }
      }
    })();
    return () => { cancelled = true; };
  }, [chart]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setScale(s => Math.max(0.2, Math.min(5, s + delta)));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    setDragging(true);
    setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
  }, [translate]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging) return;
    setTranslate({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  }, [dragging, dragStart]);

  const handleMouseUp = useCallback(() => setDragging(false), []);

  const fitToView = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => setScale(s => Math.min(5, s + 0.25)), []);
  const zoomOut = useCallback(() => setScale(s => Math.max(0.2, s - 0.25)), []);

  if (error) {
    return (
      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.50' : 'grey.900', overflow: 'auto' }}>
        <Typography sx={{ fontSize: 11, color: 'warning.main', mb: 0.5 }}>Diagram rendering failed</Typography>
        <pre style={{ margin: 0, fontSize: 11, fontFamily: '"JetBrains Mono", monospace', whiteSpace: 'pre-wrap', color: theme.palette.text.disabled }}>
          <code>{chart}</code>
        </pre>
      </Box>
    );
  }

  if (!svgContent) {
    return (
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Rendering diagram...</Typography>
      </Box>
    );
  }

  const diagramView = (isFullscreen: boolean) => (
    <Box
      ref={isFullscreen ? undefined : containerRef}
      sx={{
        position: 'relative',
        border: isFullscreen ? 'none' : '1px solid',
        borderColor: 'divider',
        borderRadius: isFullscreen ? 0 : 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        height: isFullscreen ? '100%' : compact ? 300 : 400,
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* SVG container with transform */}
      <Box
        ref={isFullscreen ? undefined : svgWrapRef}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          transformOrigin: 'center center',
          transition: dragging ? 'none' : 'transform 0.15s ease-out',
          '& svg': { maxWidth: 'none', maxHeight: 'none' },
        }}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />

      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={0.25}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: alpha(theme.palette.common.black, 0.6),
          borderRadius: 1.5,
          px: 0.5,
          py: 0.25,
          backdropFilter: 'blur(8px)',
        }}
      >
        <IconButton size="small" onClick={zoomIn} sx={{ color: theme.palette.common.white, p: 0.5 }}>
          <ZoomInIcon size={compact ? 16 : 18} />
        </IconButton>
        <IconButton size="small" onClick={zoomOut} sx={{ color: theme.palette.common.white, p: 0.5 }}>
          <ZoomOutIcon size={compact ? 16 : 18} />
        </IconButton>
        <IconButton size="small" onClick={fitToView} sx={{ color: theme.palette.common.white, p: 0.5 }}>
          <FitIcon size={compact ? 16 : 18} />
        </IconButton>
        {!isFullscreen && (
          <IconButton size="small" onClick={() => setFullscreen(true)} sx={{ color: theme.palette.common.white, p: 0.5 }}>
            <FullscreenIcon size={compact ? 16 : 18} />
          </IconButton>
        )}
        {isFullscreen && (
          <IconButton size="small" onClick={() => setFullscreen(false)} sx={{ color: theme.palette.common.white, p: 0.5 }}>
            <CloseIcon size={compact ? 16 : 18} />
          </IconButton>
        )}
      </Stack>

      {/* Zoom level indicator */}
      <Typography sx={{
        position: 'absolute', bottom: 6, right: 10,
        fontSize: 10, color: alpha(theme.palette.common.white, 0.4),
        pointerEvents: 'none',
      }}>
        {Math.round(scale * 100)}%
      </Typography>
    </Box>
  );

  return (
    <>
      {diagramView(false)}
      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{ sx: { bgcolor: theme.palette.background.paper } }}
      >
        {diagramView(true)}
      </Dialog>
    </>
  );
}
